/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import clsx from "clsx";
import React, {Fragment, useState} from "react";
import {Menu, Transition} from "@headlessui/react";
import {CheckIcon, ChevronDownIcon, XMarkIcon} from "@heroicons/react/24/solid";
import {useTranslation} from "react-i18next";
import {useDebouncedCallback} from "use-debounce";
import TableFilterRemoveButton
    from "pages/Front/components/Tanstack/components/Filters/components/TableFilterRemoveButton";
import {Column, Table} from "@tanstack/react-table";

interface TableFilterItemOption {
    id: string;
    name: string;
}

interface TableFiltersellerOptionsProps {
    sellerOptions: TableFilterItemOption[];
    selected: string[];
    setFilterValuesHandler: (val: string) => void;
}

const TableFilterItemOptions = (props: TableFiltersellerOptionsProps) => {
    return (
        <>
            {props.sellerOptions
                .map((item, index) => {
                    const isSelected = props.selected.includes(item.id);
                    return (
                        <div key={index}
                             className={clsx("col-span-1 flex items-center justify-start cursor-pointer group", isSelected ? "" : "hover:text-blue-400")}
                             onClick={() => props.setFilterValuesHandler(item.id)}>
                            <span
                                className={clsx("w-5 h-5 border border-black flex-none rounded-sm mr-1.5 flex items-center justify-center", isSelected ? "" : "group-hover:border-blue-400")}>
                                {isSelected && (
                                    <>
                                        <CheckIcon
                                            className={clsx("h-4 w-4 flex-none text-blue-400 group-hover:hidden")} />
                                        <XMarkIcon
                                            className={"h-4 w-4 flex-none text-red-400 hidden group-hover:block"} />
                                    </>
                                )}
                            </span>
                            {item.name}
                        </div>
                    );
                })
            }
        </>
    );
};

interface TableFilterSellersProps {
    column: Column<any, unknown>;
    table: Table<any>;
    className?: string;
    disabled?: boolean;
}

const TableFilterSellers = (props: TableFilterSellersProps) => {
    const {t} = useTranslation();

    const sellerOptions: TableFilterItemOption[] = [
        {id: "Lea", name: "Lea"},
        {id: "Daniel", name: "Daniel"},
        {id: "Silas", name: "Silas"},
        {id: "Back Office", name: "Backoffice"},
    ];

    const values: string[] = props.column.getFilterValue() as string[];
    const valuesReversed = values ? Object.values(values).flat() : [];
    const [selected, setSelected] = useState<string[]>(
        valuesReversed ? sellerOptions && sellerOptions.filter(item => valuesReversed.includes(item.id)).map(item => item.id) : [],
    );

    const debouncedChangeHandler = useDebouncedCallback((data) => props.column.setFilterValue(data), 500);

    const setFilterValuesHandler = (item: string) => {
        const isInArray = selected.find(prevItem => prevItem === item);
        const newValues = isInArray ? selected.filter(prevItem => prevItem !== item) : [...selected, item];
        setSelected(newValues);
        debouncedChangeHandler(newValues);
    };

    const removeFiltersHandler = () => {
        setSelected([]);
        debouncedChangeHandler([]);
    };

    return (
        <div className={clsx("TableFilterSellers  flex flex-row items-end h-full w-36 mt-1 relative", props.className)}>
            <div className={"relative z-[1]"}>
                <Menu as="div" className="relative inline-block text-left">
                    {({open}) => (
                        <>
                            <div>
                                <Menu.Button
                                    className={clsx(
                                        "w-36 ui-open:text-black relative cursor-pointer rounded inline-flex items-center px-1.5 py-1 text-base transition duration-150 border border-gray-500 text-dark-800 bg-gray-200 hover:bg-white group-hover:bg-white",
                                        open && "outline-1 outline-blue-400 -outline-offset-1",
                                        selected.length > 0 && "bg-white !text-blue-400",
                                    )}
                                >
                                    <span
                                        className={clsx("block truncate w-full pr-4 text-left")}>
                                        {t("Selected")}: {selected.length}
                                    </span>
                                    <span className={clsx(
                                        "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-0.5 text-gray-800 group-hover:text-blue-400",
                                        open && "text-blue-400",
                                    )}>
                                        <ChevronDownIcon
                                            className={clsx(
                                                "ml-2 h-4 w-4 text-black",
                                            )}
                                        />
                                    </span>
                                </Menu.Button>
                            </div>
                            <Transition
                                as={Fragment}
                                leave={"transition ease-in duration-100"}
                                leaveFrom={"opacity-100"}
                                leaveTo={"opacity-0"}
                            >
                                <Menu.Items
                                    className={"absolute z-10 mt-0.5 max-h-60 font-normal w-80 overflow-auto rounded-md bg-white py-1 text-base sm:text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none scrollbar-table-filters scrollbar"}
                                >
                                    <div className={"px-4 pt-2"}>
                                        <div className={"durationOptions border-b"}>
                                            <h4 className={"mb-1.5 font-medium"}>{t("Type")}</h4>
                                            <div className={"grid grid-cols-2 gap-2 justify-start items-center pb-2.5"}>
                                                <TableFilterItemOptions
                                                    sellerOptions={sellerOptions}
                                                    selected={selected}
                                                    setFilterValuesHandler={setFilterValuesHandler}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Menu.Items>
                            </Transition>
                        </>
                    )}
                </Menu>
            </div>
            <TableFilterRemoveButton className={"mb-2"} inputValue={selected.length !== 0}
                                     onClick={removeFiltersHandler} />
        </div>
    );
};

export default TableFilterSellers;